var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
// External dependencies
import { useEffect, useContext, } from 'react';
import { Box, Grid, Typography, } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
// Internal dependencies
import { axiosClient } from '../axios';
import { TextFieldSG, ButtonSG } from '../components';
import { ENDPOINTS, LOG_LEVELS } from '../constants';
import { UserContext, getAuthHeader } from '../auth';
import { SAMPLE_PROMPTS, PROMPT_CATEGORIES } from './constants';
import { appStyles } from '../App.styles';
/** Prompt Form */
export const PromptForm = function (props) {
    var _a;
    const user = useContext(UserContext);
    const { t, i18n } = useTranslation();
    // Extract props
    const { promptForm, setPrompts, setCustomPrompts, promptCategory, setCustomFormOpen, customFormOpen, sx, } = props;
    // Validate schema
    const validationSchema = yup.object({
        promptName: yup
            .string()
            .required('This section cannot be empty')
            .max(20, 'Please enter no more than 20 characters'),
        promptContent: yup
            .string()
            .required('This section cannot be empty')
    });
    /** Initialize values, validation schema, submit action */
    const formik = useFormik({
        initialValues: {
            promptName: promptForm === null || promptForm === void 0 ? void 0 : promptForm.promptName,
            promptContent: promptForm === null || promptForm === void 0 ? void 0 : promptForm.promptContent,
            promptId: promptForm === null || promptForm === void 0 ? void 0 : promptForm.promptId,
        },
        validationSchema: validationSchema,
        validateOnChange: false,
        onSubmit: (values) => __awaiter(this, void 0, void 0, function* () {
            var _b, _c, _d, _e, _f;
            try {
                // Get body request
                const body = Object.assign({}, values);
                // Get auth header
                const authHeader = yield getAuthHeader(user);
                // Get headers
                const headers = Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language });
                let res = null;
                // Check if update or create a new prompt
                // If update prompt
                if (values === null || values === void 0 ? void 0 : values.promptId) {
                    res = yield axiosClient.put(`${process.env.SERVER_URL}${ENDPOINTS.CHAT}${ENDPOINTS.UPDATE_PROMPT}/${values.promptId}`, body, { headers });
                }
                else {
                    // If create a new prompt
                    res = yield axiosClient.post(`${process.env.SERVER_URL}${ENDPOINTS.CHAT}${ENDPOINTS.CREATE_PROMPT}`, body, { headers });
                }
                ;
                // Get updated prompts
                const prompts = (_c = (_b = res === null || res === void 0 ? void 0 : res.data) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.prompts;
                if (promptCategory === PROMPT_CATEGORIES[PROMPT_CATEGORIES.length - 1]) {
                    // If category is custom
                    setPrompts([...prompts]);
                }
                else {
                    // If category is all categories
                    setPrompts([...prompts, ...SAMPLE_PROMPTS]);
                }
                ;
                // Update custom prompts
                setCustomPrompts([...prompts]);
                // Close the custom form
                setCustomFormOpen(false);
                // Clear the form
                formik.setValues({
                    promptName: '',
                    promptContent: '',
                    promptId: '',
                });
            }
            catch (err) {
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: `promptForm, onSubmit, ${err === null || err === void 0 ? void 0 : err.message}`,
                    metadata: {
                        env: process.env.ENV,
                        userId: user === null || user === void 0 ? void 0 : user.uid,
                        email: user === null || user === void 0 ? void 0 : user.email,
                        userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                    },
                });
                // Check if the error is from axios
                if ((_d = err === null || err === void 0 ? void 0 : err.response) === null || _d === void 0 ? void 0 : _d.status) {
                    // If yes, notify user with the server error message
                    toast.error((_f = (_e = err === null || err === void 0 ? void 0 : err.response) === null || _e === void 0 ? void 0 : _e.data) === null || _f === void 0 ? void 0 : _f.message);
                    return;
                }
                ;
                toast.error(t('Something went wrong. We are figuring it out.'));
            }
            ;
        }),
    });
    useEffect(() => {
        formik.setValues(promptForm);
    }, [promptForm]);
    /**
     * Handle delete prompt
     * @param {string} promptId
     * @returns {Promise<void>}
     */
    const handleDeletePrompt = (promptId) => __awaiter(this, void 0, void 0, function* () {
        var _g, _h, _j, _k;
        try {
            // Get auth header
            const authHeader = yield getAuthHeader(user);
            // Get headers
            const headers = Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language });
            // Delete the prompt
            const res = yield axiosClient.delete(`${process.env.SERVER_URL}${ENDPOINTS.CHAT}${ENDPOINTS.DELETE_PROMPT}/${promptId}`, { headers });
            // Get the new prompts list
            const { prompts } = (_g = res === null || res === void 0 ? void 0 : res.data) === null || _g === void 0 ? void 0 : _g.data;
            // Clear the form
            formik.setValues({
                promptName: '',
                promptContent: '',
                promptId: '',
            });
            // Close the form
            setCustomFormOpen(false);
            // Update prompts list
            if (promptCategory === PROMPT_CATEGORIES[PROMPT_CATEGORIES.length - 1]) {
                // If the category is custom
                setPrompts([...prompts]);
            }
            else {
                // If the category is all categories
                setPrompts([...prompts, ...SAMPLE_PROMPTS]);
            }
            ;
            // Update custom prompts list
            setCustomPrompts([...prompts]);
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `promptForm, handleDeletePrompt, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
            // Check if the error is from axios
            if ((_h = err === null || err === void 0 ? void 0 : err.response) === null || _h === void 0 ? void 0 : _h.status) {
                // If yes, notify user with the server error message
                toast.error((_k = (_j = err === null || err === void 0 ? void 0 : err.response) === null || _j === void 0 ? void 0 : _j.data) === null || _k === void 0 ? void 0 : _k.message);
                return;
            }
            ;
            toast.error(t('Something went wrong. We are figuring it out.'));
        }
        ;
    });
    return (_jsxs(Box, Object.assign({ component: "form", onSubmit: formik.handleSubmit, sx: Object.assign({ border: '1px solid #BFC9C3', borderRadius: '16px', padding: '1rem', margin: '1.5rem 0 0', display: customFormOpen ? 'block' : 'none' }, sx) }, { children: [_jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text22), { color: '#191C1B', padding: '0 0 2rem' }) }, { children: t('Edit Custom Prompt') })), _jsx(TextFieldSG
            // Company text field
            , { 
                // Company text field
                name: 'promptName', label: t('Set a prompt name'), placeholder: t('Blog Generator'), value: formik.values.promptName, onChange: formik.handleChange, error: formik.touched.promptName && Boolean(formik.errors.promptName), helperText: formik.touched.promptName && formik.errors.promptName, xstyles: { padding: '0 0 2rem' } }), _jsx(TextFieldSG
            // Company text field
            , { 
                // Company text field
                name: 'promptContent', label: t('Enter your prompt'), placeholder: t('Be specific and give steps. Use bracket for [placeholders].'), value: formik.values.promptContent, onChange: formik.handleChange, error: formik.touched.promptContent && Boolean(formik.errors.promptContent), helperText: formik.touched.promptContent && formik.errors.promptContent, xstyles: { padding: '0 0 2rem' }, multiline: true, maxRows: 8 }), _jsxs(Grid, Object.assign({ container: true, spacing: 1 }, { children: [_jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(ButtonSG, Object.assign({ xstyles: { display: ((_a = formik === null || formik === void 0 ? void 0 : formik.values) === null || _a === void 0 ? void 0 : _a.promptId) ? 'block' : 'none' }, variant: "contained", onClick: () => { handleDeletePrompt(formik.values.promptId); } }, { children: t('Delete') })) })), _jsx(Grid, Object.assign({ item: true }, { children: _jsx(ButtonSG, Object.assign({ variant: "contained", type: "submit" }, { children: t('Save') })) })), _jsx(Grid, Object.assign({ item: true }, { children: _jsx(ButtonSG, Object.assign({ variant: "contained", onClick: () => {
                                setCustomFormOpen(false);
                                formik.setValues({ promptName: '', promptContent: '', promptId: '' });
                            } }, { children: t('Cancel') })) }))] }))] })));
};
